

import React, { useState, useEffect } from 'react';

// --- ICONS AND UI COMPONENTS (Self-contained for easy copy-paste) ---
const Save: React.FC<{ className?: string }> = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> { variant?: 'default' | 'destructive' | 'outline' }
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant = 'default', ...props }, ref) => {
    const variants = {
      default: "bg-green-500 text-white hover:bg-green-600",
      destructive: "bg-red-600 text-white hover:bg-red-700",
      outline: "border border-slate-300 bg-white hover:bg-slate-100",
    };
    return <button className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ${variants[variant]} ${className}`} ref={ref} {...props} />;
});
Button.displayName = 'Button';

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
  <input className={`flex h-10 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />
));
Input.displayName = 'Input';

// --- TypeScript Interfaces ---
export interface NewSegmentData {
  segmentName: string;
  status: 'Active' | 'Inactive';
  // length: string;
  // width: string;
  numberOfSets: string;
}

// This interface is used to pass data (including id) for editing
export interface ISizeSegmentDataForForm extends NewSegmentData {
    id?: string;
}

interface CreateSizeSegmentFormProps {
  onSave: (data: NewSegmentData, id?: string) => void;
  onCancel: () => void;
  initialData?: ISizeSegmentDataForForm | null;
}

export const CreateSizeSegmentForm: React.FC<CreateSizeSegmentFormProps> = ({ onSave, onCancel, initialData }) => {
  const [formData, setFormData] = useState<NewSegmentData>({
    segmentName: '',
    status: 'Active',
    // length: '',
    // width: '',
    numberOfSets: '1',
  });

  const isEditing = !!initialData;

  useEffect(() => {
    if (isEditing) {
      setFormData({
        segmentName: initialData.segmentName,
        status: initialData.status,
        // length: String(initialData.length),
        // width: String(initialData.width),
        numberOfSets: String(initialData.numberOfSets),
      });
    }
  }, [initialData, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData, initialData?.id);
  };

  return (
    <div className="animate-fade-in">
        <form onSubmit={handleFormSubmit} className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                    {isEditing ? 'Edit Size Segment' : 'Create Size Segment'}
                </h1>
                <p className="text-gray-500 mt-1">Configure size segment dimensions and set details</p>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
                    <Button type="submit" className="bg-green-500 hover:bg-green-600 text-white">
                        <Save className="w-4 h-4 mr-2" />
                        {isEditing ? 'Update Segment' : 'Save Segment'}
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Segment Name</label>
                    <Input type="text" name="segmentName" value={formData.segmentName} onChange={handleChange} placeholder="e.g., 44 x 52" required />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select name="status" value={formData.status} onChange={handleChange} className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400">
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    </select>
                </div>
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Length</label>
                    <Input type="number" name="length" value={formData.length} onChange={handleChange} placeholder="44" min="0" required />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
                    <Input type="number" name="width" value={formData.width} onChange={handleChange} placeholder="52" min="0" required />
                </div> */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of pieces</label>
                    <Input type="number" name="numberOfSets" value={formData.numberOfSets} onChange={handleChange} placeholder="1" min="1" required />
                </div>
                </div>
            </div>
        </form>
    </div>
  );
};

